import { useFetch } from '../../hooks/useFetch'
import api from '../../services/api'
import StatCard from './components/StatCard'
import RecentMessages from './components/RecentMessages'
import QuickActions from './components/QuickActions'
import './Dashboard.css'

interface Article {
  id: number
  title: string
  status: string
}

interface Document {
  id: number
  title: string
}

interface TeamMember {
  id: number
  name: string
}

interface Message {
  id: number
  name: string
  email: string
  subject: string
  message: string
  created_at: string
  is_read: boolean
}

interface ArticlesResponse {
  data: Article[]
  pagination: {
    page: number
    limit: number
    total: number
    total_pages: number
  }
}

function Dashboard() {
  // Fetch articles
  const {
    data: articlesResponse,
    loading: articlesLoading,
    error: articlesError,
  } = useFetch<ArticlesResponse>(() => api.get('/articles'), [])

  // Fetch documents
  const {
    data: documents,
    loading: documentsLoading,
    error: documentsError,
  } = useFetch<Document[]>(() => api.get('/documents'), [])

  // Fetch team members
  const {
    data: teamMembers,
    loading: teamLoading,
    error: teamError,
  } = useFetch<TeamMember[]>(() => api.get('/team'), [])

  // Fetch messages
  const {
    data: messages,
    loading: messagesLoading,
    error: messagesError,
  } = useFetch<Message[]>(() => api.get('/messages'), [])

  // Extract articles array from response
  const articles = articlesResponse?.data || []

  // Calculate statistics
  const publishedArticlesCount = articles.filter((a) => a.status === 'published').length
  const documentsCount = documents ? documents.length : 0
  const teamMembersCount = teamMembers ? teamMembers.length : 0
  const unreadMessagesCount = messages
    ? messages.filter((m) => !m.is_read).length
    : 0

  // Get last 5 messages for recent messages widget
  const recentMessages = messages ? messages.slice(0, 5) : []

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Tableau de bord</h1>
        <p className="dashboard-subtitle">
          Bienvenue dans l'espace d'administration
        </p>
      </div>

      {/* Statistics Grid */}
      <div className="stats-grid">
        <StatCard
          title="Articles publiés"
          value={publishedArticlesCount}
          icon="📰"
          color="blue"
          loading={articlesLoading}
        />
        <StatCard
          title="Documents"
          value={documentsCount}
          icon="📄"
          color="green"
          loading={documentsLoading}
        />
        <StatCard
          title="Membres de l'équipe"
          value={teamMembersCount}
          icon="👥"
          color="purple"
          loading={teamLoading}
        />
        <StatCard
          title="Messages non lus"
          value={unreadMessagesCount}
          icon="✉️"
          color="orange"
          loading={messagesLoading}
        />
      </div>

      {/* Error Messages */}
      {(articlesError || documentsError || teamError || messagesError) && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <div>
            Erreur lors du chargement de certaines données. Veuillez rafraîchir
            la page.
          </div>
        </div>
      )}

      {/* Dashboard Content Grid */}
      <div className="dashboard-grid">
        <RecentMessages messages={recentMessages} loading={messagesLoading} />
        <QuickActions />
      </div>
    </div>
  )
}

export default Dashboard
